import { createWeb3Modal, useWeb3Modal } from '@web3modal/wagmi/react'
import { formatRelative, intervalToDuration, min } from 'date-fns'
import { cloneDeep, isEmpty, isEqual, toString, trim } from 'lodash'
import React, { CSSProperties, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import styled, { createGlobalStyle, css, keyframes } from 'styled-components'
import {
  Address, createPublicClient, createWalletClient, custom, erc20Abi, getContract, maxUint256, parseEther, parseUnits
} from 'viem'
import { waitForTransactionReceipt } from 'viem/actions'
import { useAccount, useDisconnect } from 'wagmi'
import WaveSvg from '../animation/Wave.svg'
import WaveSvg2 from '../animation/Wave2.svg'
import WaveSvg3 from '../animation/Wave3.svg'
import Radom, {
  IDiscountCode, IManagedPaymentMethodDetails, IManagedPaymentStatus, IManagedTokenDelegatePaymentMethodDetails,
  InputFieldWithValue, PriceQuote
} from '../api/Radom'
import Copy from '../icons/Copy'
import GasPump from '../icons/GasPump'
import { formatSubscriptionDuration, RADOM_COLORS } from '../util/Constants'
import { getMethod, isAutopayEnabled, ManagedPaymentMethod, NetworkToChainMap, projectId, wagmiConfig } from '../util/Managed'
import { convertValue, getTotalSum } from '../util/Products'
import { generateQRCode } from '../util/QRCode'
import { Token } from '../util/Tokens'
import { PrimaryButton, SecondaryButton } from './Button'
import Checkbox from './Checkbox'
import {
  CheckoutData, TravelRuleFields, TravelRuleFieldsCollected, emptyTravelRuleFields,
  zeroTravelRuleFieldsCollected
} from './Checkout'
import { Cookie, InputLabel, TextInput } from './Input'
import SolanaWallet from './SolanaWallet'
import Spinner from './Spinner'
import TravelRuleFieldsWidget from './TravelRuleFieldsWidget'
import { Chevron } from '../icons/Chevron'
import EthLogo from '../images/EthLogo.svg'
import { errorToast } from '../util/Util'

export function formatSecondsToMinutesAndSecondsString(totalSeconds: number): string {
  const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 })

  const zeroPad = (num): string => String(num).padStart(2, '0')

  const formatted = [
    duration.days,
    duration.hours,
    duration.minutes,
    duration.seconds
  ]
    .filter((a, i) => i <= 1 ? !!a : true)
    .map(zeroPad)
    .join(':')

  return formatted
}

const Styles = createGlobalStyle`
  @property --angle {
    syntax: '<angle>';
    initial-value: 90deg;
    inherits: true;
  }

  :root {
    --d: 2500ms;
    --c1: rgba(0, 0, 0, 0.5);
    --c2: rgba(0, 0, 0, 0.1);
  }
`

const ConnectedWalletContainer = styled.div`
  box-shadow: 0 0 3px ${RADOM_COLORS.GRAY_DARK};
  border-radius: 5px;
`

export const QRCodeWrapper = styled.div`
  border-radius: 10px;
  overflow: hidden;
  width: fit-content;
  box-shadow: 0 5px 5px ${RADOM_COLORS.GRAY_DARK};
  transition: 0.2s ease all;
  cursor: pointer;
  &:active {
    transform: scale(0.99);
  }
`

export const QRCodeWrapperInner = styled.div`
  width: fit-content;
  border-radius: 10px;
  box-shadow: 0 5px 5px ${RADOM_COLORS.GRAY_DARK};
  padding: 10px;
  font-size: 0;
`

export const WrapperIn = keyframes`
  from {
    transform: translateY(-25%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const WrapperAnimation = css`
  animation: ${WrapperIn} 0.2s ease;
`

const Wrapper = styled.div<{ disableAnimation?: boolean }>`
  flex-grow: 1;
  ${({ disableAnimation }) => !disableAnimation && WrapperAnimation}
`

const Container = styled.div<{ disableAnimation?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  ${({ disableAnimation }) => !disableAnimation && WrapperAnimation}
  // animation: ${WrapperIn} 0.2s ease;
`

export const TokenSelector = styled.div<{ isSelected: boolean, isDisabled: boolean, hoverEffect?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 15px;
  border-radius: ${({ hoverEffect }) => hoverEffect ? '0px' : '5px'};
  cursor: pointer;
  border-bottom: 1px solid;
  ${({ isSelected }) => isSelected && 'border: 1px solid;'}
  border-color: ${({ isSelected }) => isSelected ? RADOM_COLORS.NICE_GREEN : RADOM_COLORS.GRAY_DARK};
  box-shadow: ${({ isSelected }) => isSelected && `0 0 3px ${RADOM_COLORS.NICE_GREEN}`};
  opacity: ${({ isSelected }) => isSelected ? '1' : '0.3'};
  transition: 0.2s ease all;
  user-select: none;

  ${({ isSelected }) => isSelected && `
    position: sticky;
    top: 0;
    bottom: 0;
    background: white;
    z-index: 1;
  `}

  ${({ isDisabled }) => isDisabled && 'opacity: 0.5;'}

  &:hover {
    opacity: ${({ isSelected }) => isSelected ? 1 : 0.6};
    ${({ hoverEffect }) => hoverEffect && `
      background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
    `}
  }

  &:active {
    opacity: ${({ isSelected }) => isSelected ? 1 : 0.8};
    transform: scale(0.98);
  }
`

export const WaveContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  z-index: -1;
  overflow: hidden;
`

const WaveAnim = keyframes`
  0% {
    margin-left: 0;
  }
  100% {
    margin-left: -1600px;
  }
`

export const Wave = styled.div`
  background: url(${WaveSvg as string}) repeat-x;
  position: absolute;
  width: 6400px;
  height: 50%;
  top: calc(50% - 100px);
  animation: ${WaveAnim} 18s cubic-bezier( 0.36, 0.45, 0.63, 0.53) infinite;
`

const WaveAnim2 = keyframes`
  0% {
    margin-left: 0;
  }
  100% {
    margin-left: -3200px;
  }
`

export const Wave2 = styled(Wave)`
  background: url(${WaveSvg2 as string}) repeat-x;
  animation: ${WaveAnim2} 15s cubic-bezier( 0.36, 0.45, 0.63, 0.53) infinite;
`
const WaveAnim3 = keyframes`
  0% {
    margin-left: -800px;
  }
  100% {
    margin-left: -2400px;
  }
`

export const Wave3 = styled(Wave)`
  background: url(${WaveSvg3 as string}) repeat-x;
  animation: ${WaveAnim3} 6s cubic-bezier( 0.36, 0.45, 0.63, 0.53) infinite;
`

const Expand = keyframes`
  0% {
    transform: scaleY(0%);
  }
  100% {
    transform: scaleY(100%);
  }
`

const PaymentDetectedContainer = styled.div`
  animation: ${Expand} 0.2s cubic-bezier(0, 1, 0, 1);
  transform-origin: 0 0;
  border-radius: 15px;
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  overflow: hidden;
`

const rotate = keyframes`
  100% { transform: rotate(1turn); }
`

const LoaderWrapper = styled.div`
  width: 50px;
  aspect-ratio: 1;
  padding: 2px;
  border-radius: 50%;
  opacity: 0.8;
  background:
    radial-gradient(farthest-side, ${RADOM_COLORS.BLUE5} 94%, #0000) top/8px 8px no-repeat,
    conic-gradient(#0000 30%, ${RADOM_COLORS.INFO});
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 8px), #000 0);
  animation: ${rotate} 1s infinite linear;
`

interface Props {
  onClick: () => void
  style?: CSSProperties
  iconColor?: string
}

export const CopyButton = ({ onClick, style, iconColor = RADOM_COLORS.GRAY_DARKEST }: Props): ReactElement => {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 3000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  return <SecondaryButton
    onClick={() => { setCopied(true); onClick() }}
    style={{
      padding: '2px 4px',
      fontSize: 10,
      textTransform: 'uppercase',
      background: RADOM_COLORS.GRAY_MED,
      color: RADOM_COLORS.GRAY_DARKEST,
      cursor: 'pointer',
      borderRadius: 5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      border: 'none',
      ...style
    }}>
    <span>{copied ? 'Copied' : 'Copy'}</span>
    <Copy style={{ height: 10 }} stroke={iconColor} />
  </SecondaryButton>
}

export interface CheckoutParams {
  selfCustodial?: {
    useRadomBalance: boolean
    selectedChainId: number
    selectedToken: Token
  }
  managed?: {
    selectedMethod: ManagedPaymentMethod
  }
  completeTokenDelegate?: {
    paymentId: string
    signature: string
  }
  priceQuotes: PriceQuote[]
  subscriptionOptions?: {
    emailInvoice?: {
      subscriptionType: SubscriptionType.EmailInvoice
      emailAddress: string

    }
    autopay?: {
      subscriptionType: SubscriptionType.Autopay
      address: string
    }
  }
  travelRuleFields?: TravelRuleFields
}

interface IProps {
  checkoutData: CheckoutData
  currency: string
  isPreProcessingPayment: boolean
  isSuccess: boolean
  managedPaymentDetails?: IManagedPaymentMethodDetails
  tokenDelegatePaymentDetails?: IManagedTokenDelegatePaymentMethodDetails
  successElement: React.ReactNode
  inputsElement?: React.ReactNode
  errorMessage?: string
  discount?: IDiscountCode
  onCheckout: (params: CheckoutParams) => void | Promise<void>
  onQuoteUpdate: () => void
  posMode?: boolean
  onInputFieldsChange?: (inputs: InputFieldWithValue[]) => void
  disableAnimation?: boolean
  paymentStatus?: IManagedPaymentStatus

  // A set of flags to indicate which travel rule fields have already been collected
  travelRuleFieldsCollected?: TravelRuleFieldsCollected

  // Travel rule fields should be hidden when previewing the transaction
  hideTravelRuleFields?: boolean
}

export enum CheckoutState {
  CollectTravelRuleFields,
  Checkout,
}

enum SubscriptionCheckoutState {
  Select,
  Confirm,
  Allowance,
  EmailInvoicePay
}

export enum SubscriptionType {
  EmailInvoice,
  Autopay
}

const DEFAULT_FIAT_CURRENCY = 'USD'

export const determineTrfCollectionState = (trfFlagObj: TravelRuleFieldsCollected): CheckoutState => {
  const {
    accountType,
    companyNameCollected,
    addressLine1Collected,
    townOrCityCollected,
    postalCodeCollected,
    stateOrProvinceCollected,
    dateOfBirthCollected,
    ...otherFields
  } = trfFlagObj

  const addressMissing = !dateOfBirthCollected && (
    !addressLine1Collected ||
    !townOrCityCollected ||
    !postalCodeCollected ||
    !stateOrProvinceCollected
  )

  const needsCompany = accountType !== 'Individual' && !companyNameCollected

  const needsOther = Object.entries(otherFields).some(([key, value]) => key !== 'accountType' && !value)

  return addressMissing || needsCompany || needsOther
    ? CheckoutState.CollectTravelRuleFields
    : CheckoutState.Checkout
}

// ---------------------------------------------------------------------------------------------------------------------
// This widget is used by multiple transaction types.
// Each has a different way of handling the collection of travel rule field (TRF) information
//
// * Payment Links      Multiple Use  TRFs collected at checkout
// * Donation Links     Multiple Use  TRFs collected at checkout
// * Invoices           Single Use    Zero or more TRFs created by merchant. Remaining TRFs collected at checkout
// * Payment Sessions   Single Use    Zero or more TRFs created by merchant. Remaining TRFs collected at checkout
// * Checkout Sessions  Single Use    Zero or more TRFs created by merchant. Remaining TRFs collected at checkout
// ---------------------------------------------------------------------------------------------------------------------
const PayWidget = (props: IProps): React.ReactElement => {
  const [localCheckoutData, setLocalCheckoutData] = useState({} as any)

  const [selectedMethod, setSelectedMethod] = useState<ManagedPaymentMethod>()
  const [selectedTicker, setSelectedTicker] = useState<string>()
  const [selectedChainName, setSelectedChainName] = useState<string>()
  const [selectedTokenImageUrl, setSelectedTokenImageUrl] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)

  const [priceQuotes, setPriceQuotes] = useState<PriceQuote[]>([])
  const [quoteExpiryTimestamp, setQuoteExpiryTimestamp] = useState<Date>(new Date())
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [quoteSecondsRemaining, setQuoteSecondsRemaining] = useState(0)
  const [isLoadingPriceQuotes, setIsLoadingPriceQuotes] = useState(false)

  const { disconnect } = useDisconnect()
  const { address, isConnected, connector } = useAccount()

  const isOpenBanking = props.checkoutData?.gateway?.openBanking !== undefined

  if (typeof window !== 'undefined' && !(window as any).__W3M_INITIALIZED__ && !isOpenBanking && props.checkoutData) {
    createWeb3Modal({
      wagmiConfig,
      projectId
    })
    ;(window as any).__W3M_INITIALIZED__ = true
  }

  const { open } = useWeb3Modal()

  const [localInputFields, setLocalInputFields] = useState([] as InputFieldWithValue[])
  const [checkoutState, setCheckoutState] = useState<CheckoutState>(
    props.hideTravelRuleFields
      ? CheckoutState.Checkout
      : props.travelRuleFieldsCollected
        ? determineTrfCollectionState(props.travelRuleFieldsCollected)
        : CheckoutState.CollectTravelRuleFields
  )
  const [travelRuleFields, setTravelRuleFields] = useState(emptyTravelRuleFields())

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const [subscriptionEmailAddress, setSubscriptionEmailAddress] = useState('')

  useEffect(() => {
    if (typeof window === 'undefined' || (window as any).__W3M_INITIALIZED__) {
      return
    }

    if (props.checkoutData && !props.checkoutData.gateway?.openBanking) {
      createWeb3Modal({
        wagmiConfig,
        projectId
      })
      ;(window as any).__W3M_INITIALIZED__ = true
    }
  }, [props.checkoutData])

  function formatCrypto8(value: number | string): string {
    const maxDp = 8
    const v = Number(value)
    if (!Number.isFinite(v)) return '—'

    const threshold = Math.pow(10, -maxDp)
    if (v > 0 && v < threshold) {
      return `< ${'0.' + '0'.repeat(maxDp - 1)}1`
    }
    const fixed = v.toFixed(maxDp)
    const trimmedZeros = fixed.replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '')
    return trimmedZeros
  }

  useEffect(() => {
    if (props.checkoutData.inputFields) {
      inputRefs.current = inputRefs.current.slice(0, props.checkoutData.inputFields.length)
      setLocalInputFields(props.checkoutData.inputFields)
    }
  }, [props.checkoutData.inputFields])

  // -------------------------------------------------------------------------------------------------------------------
  const getPriceQuotes = (): void => {
    props.onQuoteUpdate()
    setIsLoadingPriceQuotes(true)

    const fromCurList = [
      ...(props.checkoutData.products?.map(p => p.product.currency) ?? []),
      ...(props.checkoutData.lineItems?.map(p => p.currency ?? DEFAULT_FIAT_CURRENCY) ?? []),
      props.currency || DEFAULT_FIAT_CURRENCY
    ]
    const { managed } = props.checkoutData.gateway
    if (managed) {
      fromCurList.push(
        DEFAULT_FIAT_CURRENCY,
        ...managed.methods.map(t => getMethod(t.network, t.token).ticker || '')
      )
    }
    const fromCur = [...new Set(fromCurList)]

    if (fromCur.length === 0) return

    Radom
      .getPriceQuotes(fromCur, fromCur)
      .then(priceQuotes => {
        const minTimestamp = min(priceQuotes.map(p => new Date(p.expiryTimestamp)))
        setQuoteExpiryTimestamp(minTimestamp)
        setQuoteSecondsRemaining((minTimestamp.getTime() - new Date().getTime()) / 1000)
        setPriceQuotes(priceQuotes)

        setIsLoadingPriceQuotes(false)
      })
  }

  // -------------------------------------------------------------------------------------------------------------------
  const [totalSum, recurringSum] = useMemo(() => {
    return getTotalSum(props.checkoutData, priceQuotes, props.currency, props.discount)
  }, [props.checkoutData, priceQuotes, props.currency, props.discount])

  // -------------------------------------------------------------------------------------------------------------------
  const recurringInterval = (): number => {
    if (props.checkoutData.products) {
      const g = props.checkoutData.products.find(p => p.product.chargingIntervalSeconds > 0)
      if (g) {
        return g.product.chargingIntervalSeconds / 60 / 60 / 24
      }
    }

    if (props.checkoutData.lineItems) {
      const g = props.checkoutData.lineItems.find(p => p.chargingIntervalSeconds > 0)
      if (g) {
        return g.chargingIntervalSeconds / 60 / 60 / 24
      }
    }

    return 0
  }

  // -------------------------------------------------------------------------------------------------------------------
  const nextBillingAt = (): string => {
    if (props.checkoutData.products) {
      const g = props.checkoutData.products.find(p => p.product.chargingIntervalSeconds > 0)
      if (g?.product.nextBillingDateAt) {
        return formatRelative(new Date(g.product.nextBillingDateAt), new Date())
      }
    }

    if (props.checkoutData.lineItems) {
      const g = props.checkoutData.lineItems.find(p => p.chargingIntervalSeconds > 0)
      if (g?.nextBillingDateAt) {
        return formatRelative(new Date(g.nextBillingDateAt), new Date())
      }
    }

    return formatRelative(
      new Date(new Date().getTime() + recurringInterval() * 60 * 60 * 24 * 1000),
      new Date()
    )
  }

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (
      isEqual(localCheckoutData.gateway, props.checkoutData.gateway) &&
      isEqual(localCheckoutData.products, props.checkoutData.products) &&
      isEqual(localCheckoutData.lineItems, props.checkoutData.lineItems) &&
      isEqual(localCheckoutData.currency, props.checkoutData.currency) &&
      isEqual(localCheckoutData.total, props.checkoutData.total)
    ) {
      return
    }

    if (props.checkoutData.gateway.managed) {
      setLocalCheckoutData(cloneDeep(props.checkoutData))
      getPriceQuotes()
    }
  }, [props.checkoutData])

  useEffect(() => getPriceQuotes(), [props.checkoutData.products?.length])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const myInterval = setInterval(() => {
      if (quoteExpiryTimestamp.getTime() <= new Date().getTime()) {
        getPriceQuotes()
      }

      setQuoteSecondsRemaining((quoteExpiryTimestamp.getTime() - new Date().getTime()) / 1000)
    }, 1000)

    return () => clearInterval(myInterval)
  }, [quoteExpiryTimestamp])

  // -------------------------------------------------------------------------------------------------------------------
  const [managedPaymentQrCode, setManagedPaymentQrCode] = useState('')
  const [isManagedEVMTxLoading, setIsManagedEVMTxLoading] = useState(false)
  const [isSigningMessage, setIsSigningMessage] = useState(false)
  const [isSettingAllowance, setIsSettingAllowance] = useState(false)
  const [allowSkipAllowance, setAllowSkipAllowance] = useState(false)
  const [gasCost, setGasCost] = useState('')
  const [isLoadingAllowance, setIsLoadingAllowance] = useState(false)

  const [managedSubscriptionState, setManagedSubscriptionState] = useState<SubscriptionCheckoutState>()
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<SubscriptionType>()
  const [receiveEmailNotifications, setReceiveEmailNotifications] = useState(false)

  const isBitcoinNetwork = useMemo(() => props.managedPaymentDetails?.network === 'Bitcoin', [props.managedPaymentDetails?.network])

  const generateManagedPaymentQrCode = async (): Promise<void> => {
    if (!props.managedPaymentDetails) {
      return
    }

    const [qrCodeData] = await generateQRCode(props.managedPaymentDetails)
    setManagedPaymentQrCode(qrCodeData)
  }

  // -------------------------------------------------------------------------------------------------------------------
  const onClickOpenWallet = async (): Promise<void> => {
    if (!props.managedPaymentDetails) {
      return
    }

    switch (props.managedPaymentDetails.network) {
    case 'Bitcoin':
    case 'Solana':
    case 'Zcash':
    case 'ZcashTestnet':
    case 'Polkadot':
    case 'PolkadotTestnet':
    case 'PolkadotAssetHub':
    case 'PolkadotAssetHubTestnet':
      break
    case 'Ethereum':
    case 'SepoliaTestnet':
    case 'Polygon':
    case 'PolygonTestnet':
    case 'BNB':
    case 'BNBTestnet':
    case 'Base':
    case 'Arbitrum':
    case 'Avalanche':
      try {
        if (!address) {
          await open()
          return
        }

        setIsManagedEVMTxLoading(true)

        const chain = NetworkToChainMap[props.managedPaymentDetails.network] as any
        const provider: any = await connector?.getProvider()

        const signer = createWalletClient({
          chain,
          transport: custom(provider)
        })

        if (connector?.switchChain) {
          const chainId = await connector.getChainId()
          if (chainId !== chain.id) {
            await connector?.switchChain({ chainId: chain.id })
          }
        }

        if (props.managedPaymentDetails.token) {
          const method = getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token)

          const contract = getContract({
            address: props.managedPaymentDetails.token as any,
            abi: erc20Abi,
            client: signer
          })

          try {
            const balance = await contract.read.balanceOf([address])
            const decimals = await contract.read.decimals()
            const parsedAmount = parseUnits(totalSum.toString(), decimals)
            if (balance < parsedAmount) {
              setIsManagedEVMTxLoading(false)
              errorToast(`Your wallet does not have enough ${selectedMethod?.name} for payment.`)
              return
            }
          } catch (err) {
            console.error('Failed to get token balance using injected provider', err)
          }

          await contract.write.transfer([
            props.managedPaymentDetails.paymentAddress as Address,
            parseUnits(props.managedPaymentDetails.amount, method.tokenInfo?.decimals ?? 6)
          ], { account: address, chain })
        } else {
          const client = createPublicClient({
            chain,
            transport: custom(provider)
          })

          try {
            const balance = await client.getBalance({ address })
            if (balance < parseEther(props.managedPaymentDetails.amount)) {
              setIsManagedEVMTxLoading(false)
              errorToast(`Your wallet does not have enough ${selectedMethod?.ticker ?? 'amount'} for payment.`)
              return
            }
          } catch (err) {
            console.error('Failed to get balance using injected provider', err)
          }

          if (!signer) return

          await signer.sendTransaction({
            account: address,
            chain,
            to: props.managedPaymentDetails.paymentAddress as Address,
            value: parseEther(props.managedPaymentDetails.amount)
          })
        }
      } catch (err) {
        setIsManagedEVMTxLoading(false)
        errorToast('Failed to approve payment. Please ensure you have enough funds.')
        console.error(err)
      }
      break
    default:
      console.error('No wallet')
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  const setManagedSubscriptionAllowance = async (): Promise<void> => {
    if (selectedMethod?.tokenInfo?.tokenType !== 'evm') {
      return
    }

    if (!address) {
      await open()
      return
    }

    setIsSettingAllowance(true)

    try {
      if (!address) return

      const chain = NetworkToChainMap[selectedMethod.tokenInfo.networkName] as any
      const provider: any = await connector?.getProvider()

      const signer = createWalletClient({
        chain,
        transport: custom(provider)
      })

      if (connector?.switchChain) {
        const chainId = await connector.getChainId()
        if (chainId !== chain.id) {
          await connector?.switchChain({ chainId: chain.id })
        }
      }

      const contract = getContract({
        address: selectedMethod.tokenInfo.tokenAddress as any,
        abi: erc20Abi,
        client: { public: signer, wallet: signer }
      })
      const balance = await contract.read.balanceOf([address])

      const parsedAmount = parseUnits(totalSum.toString(), selectedMethod.tokenInfo.decimals)
      if (balance < parsedAmount) {
        setIsSettingAllowance(false)
        errorToast(`Your wallet does not have enough ${selectedMethod?.name} for payment.`)
        return
      }

      const allowance = await contract.read.allowance([
        address,
        selectedMethod.tokenInfo.subscriptionContract as Address
      ])

      if (allowance <= BigInt(0) || allowance < parsedAmount * BigInt(300)) {
        if (selectedMethod.name === 'Tether' && allowance > 0) {
          const hash = await contract.write.approve(
            [selectedMethod.tokenInfo.subscriptionContract as any, 0n],
            {
              account: address as any,
              chain
            }
          )

          if (hash) {
            try {
              await waitForTransactionReceipt(signer, {
                hash,
                retryCount: 30,
                retryDelay: 1000
              })
            } catch (err) {
              console.error('Failed to confirm allowance tx', err)
            }
          }
        }

        const hash = await contract.write.approve(
          [selectedMethod.tokenInfo.subscriptionContract as any, maxUint256],
          {
            account: address as any,
            chain
          }
        )

        if (hash) {
          try {
            console.log('Waiting for tx receipt')
            await waitForTransactionReceipt(signer, {
              hash,
              retryCount: 30,
              retryDelay: 1000
            })
          } catch (err) {
            console.error('Failed to confirm allowance tx', err)
          }
        }
      }

      setIsSettingAllowance(false)
      setManagedSubscriptionState(SubscriptionCheckoutState.Confirm)
    } catch (err) {
      setIsSettingAllowance(false)
      errorToast('Failed to set allowance')
      console.error(err)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (props.managedPaymentDetails) {
      generateManagedPaymentQrCode()

      if (managedSubscriptionState !== undefined) {
        setManagedSubscriptionState(SubscriptionCheckoutState.EmailInvoicePay)
      }
    }
  }, [props.managedPaymentDetails])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (managedSubscriptionState === undefined) {
      setSelectedSubscriptionType(undefined)
      setReceiveEmailNotifications(false)
    }

    if (selectedMethod?.tokenInfo && selectedMethod.tokenInfo.tokenType === 'evm') {
      Radom.estimateTokenTranferGas(selectedMethod.tokenInfo.networkName, selectedMethod.tokenInfo.tokenAddress)
        .then(g => {
          setGasCost(g.gasCost)
        })
        .catch(err => console.error(err))
    }
  }, [managedSubscriptionState])

  // -------------------------------------------------------------------------------------------------------------------
  const computeAllowanceStep = async (): Promise<void> => {
    if (!props.tokenDelegatePaymentDetails) {
      return
    }

    if (selectedMethod?.tokenInfo?.tokenType !== 'evm') {
      return
    }

    if (!address || !connector) {
      await open()
      return
    }

    setIsLoadingAllowance(true)

    let signer
    const chain = NetworkToChainMap[props.tokenDelegatePaymentDetails.network] as any

    try {
      const provider: any = await connector?.getProvider()

      signer = createWalletClient({
        chain,
        transport: custom(provider)
      })

      if (connector?.switchChain) {
        const chainId = await connector.getChainId()
        if (chainId !== chain.id) {
          await connector?.switchChain({ chainId: chain.id })
        }
      }
    } catch (err) {
      setIsLoadingAllowance(false)
      errorToast(err.toString())
      return
    }

    const contract = getContract({
      address: selectedMethod.tokenInfo.tokenAddress as any,
      abi: erc20Abi,
      client: { public: signer, wallet: signer }
    })

    try {
      if (!address) return
      const allowance = await (contract as any).read.allowance([
        address,
        selectedMethod.tokenInfo.subscriptionContract as Address
      ])
      const parsedAmount = parseUnits(totalSum.toString(), 6)
      if (allowance >= parsedAmount) {
        setAllowSkipAllowance(true)
      }
    } catch (err) {
      setAllowSkipAllowance(true)
    }

    setIsLoadingAllowance(false)
    setManagedSubscriptionState(SubscriptionCheckoutState.Allowance)
  }

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (props.tokenDelegatePaymentDetails) {
      computeAllowanceStep()
    } else if (managedSubscriptionState === SubscriptionCheckoutState.Confirm) {
      setManagedSubscriptionState(SubscriptionCheckoutState.Select)
    }
  }, [props.tokenDelegatePaymentDetails])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!props.errorMessage) return
    errorToast(props.errorMessage)
  }, [props.errorMessage])

  // -------------------------------------------------------------------------------------------------------------------
  // If any or all of the travel rule fields are missing, collect these first
  const trfWidget = !props.checkoutData.isInvoice && checkoutState === CheckoutState.CollectTravelRuleFields
    ? <TravelRuleFieldsWidget
      travelRuleFieldsCollected={
        props.travelRuleFieldsCollected ?? zeroTravelRuleFieldsCollected()
      }
      onTravelRuleFieldsComplete={async collectedTrFields => {
        setCheckoutState(CheckoutState.Checkout)
        setTravelRuleFields(collectedTrFields)

        const checkoutDataId = trim(toString(props.checkoutData?.id))

        if (!isEmpty(checkoutDataId)) {
          try {
            if (props.checkoutData.isInvoice) {
              await Radom.updateTrfsForCustomerViaInvoice(checkoutDataId, collectedTrFields)
            } else {
              await Radom.upsertTrfsForCheckoutSession(checkoutDataId, collectedTrFields)
            }
          } catch (err) {
            console.error('Failed to save travel rule fields', err)
          }
        }

        // If the Name or Email address exist as custom input fields, prefill these fields with the travel rule values
        let inputFieldsUpdated = false
        const newLocalInputFields = localInputFields.map(newLif => {
          if (newLif.inputLabel === 'Email address') {
            inputFieldsUpdated = true
            newLif.value = collectedTrFields.emailAddress ?? ''
          }
          if (newLif.inputLabel === 'Name') {
            inputFieldsUpdated = true
            newLif.value = collectedTrFields.name ?? ''
          }
          return newLif
        })

        if (inputFieldsUpdated) setLocalInputFields(newLocalInputFields)
      }}
    />
    : null

  const isHexColor = (color: string | undefined): boolean | undefined => {
    if (!color) return
    return /^#?[0-9A-Fa-f]{6}$/.test(color)
  }

  const getContrastColor = (hexColor: string | undefined): string | undefined => {
    if (!hexColor) return

    const isHexColorCheck = isHexColor(hexColor)

    if (!isHexColorCheck) return

    const hex = hexColor.replace('#', '')

    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    const yiq = (r * 299 + g * 587 + b * 114) / 1000

    return yiq >= 128 ? '#000000' : '#FFFFFF'
  }

  // -------------------------------------------------------------------------------------------------------------------
  return <Wrapper disableAnimation={props.disableAnimation}>
    {props.isSuccess && props.successElement}
    {trfWidget ?? <>

      {
        !props.isSuccess && props.checkoutData.gateway.managed && <Container disableAnimation={props.disableAnimation}>

          {
            // -----------------------------------------------------------------------------------------------------------
            !props.managedPaymentDetails &&
            managedSubscriptionState === undefined &&
            <Container disableAnimation={props.disableAnimation}>
              {
                props.checkoutData.inputFields && props.checkoutData.inputFields.length > 0 &&
                !props.isSuccess &&
                <div
                  style={{
                    width: '100%',
                    display: 'grid',
                    gap: 20,
                    paddingBottom: 25,
                    borderBottom: `1px solid ${RADOM_COLORS.GRAY_MED}`
                  }}>
                  {
                    // ---------------------------------------------------------------------------------------------------
                    // Render zero or more custom input fields
                    localInputFields.map((c, i) => <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <InputLabel>{c.inputLabel === '' ? 'Input label' : c.inputLabel}</InputLabel>
                        {!c.isRequired && <Cookie>Optional</Cookie>}
                      </div>
                      <div>
                        {
                          // Since this custom input field overlaps with a travel rule field, it will be prefilled with
                          // the travel rule value and disabled for input
                          c.dataType === 'Email' &&
                          <TextInput
                            disabled={props.isPreProcessingPayment ||
                              (travelRuleFields.emailAddress !== undefined && travelRuleFields.emailAddress.length > 0)}
                            ref={el => inputRefs.current[i] = el}
                            placeholder={`Enter ${c.inputLabel.toLowerCase()}`}
                            value={c.value as string}
                            required={c.isRequired}
                            onInput={e => {
                              const newInputs = localInputFields.slice()
                              newInputs[i].value = (e.target as HTMLInputElement).value.toLowerCase()
                              props.onInputFieldsChange?.(newInputs)
                            }}
                          />
                        }
                        {
                          // If this custom input field overlaps with a travel rule field, it will be prefilled with the
                          // travel rule value and disabled for input
                          c.dataType === 'String' &&
                          <TextInput
                            disabled={props.isPreProcessingPayment ||
                              (c.inputLabel === 'Name' &&
                                travelRuleFields.name !== undefined &&
                                travelRuleFields.name.length > 0)
                            }
                            ref={el => inputRefs.current[i] = el}
                            placeholder={`Enter ${c.inputLabel.toLowerCase()}`}
                            value={c.value as string}
                            required={c.isRequired}
                            onInput={e => {
                              const newInputs = localInputFields.slice()
                              newInputs[i].value = (e.target as HTMLInputElement).value
                              props.onInputFieldsChange?.(newInputs)
                            }}
                          />
                        }
                        {
                          c.dataType === 'Number' &&
                          <TextInput
                            disabled={props.isPreProcessingPayment}
                            type="number"
                            value={c.value as number}
                            ref={el => inputRefs.current[i] = el}
                            required={c.isRequired}
                            placeholder="Enter a number"
                            onInput={e => {
                              const val = (e.target as HTMLInputElement).value
                              const newInputs = localInputFields.slice()
                              newInputs[i].value = val
                              props.onInputFieldsChange?.(newInputs)
                            }}
                          />
                        }
                      </div>
                    </div>
                    )
                  }
                </div>
              }
              <>
                <style>{`
                  @keyframes fadeSlideIn {
                    from {opacity:0;transform:translateY(6px)}
                    to {opacity:1;transform:none}
                  }
                  .pm-list {
                    animation:fadeSlideIn .15s ease-out;
                    overflow: hidden;
                  }
                  .pm-list:hover .pm-row:not(:hover){
                    filter:blur(.15px);
                    opacity:.75;
                    transition:filter .25s,opacity .25s
                  }

                  @keyframes fadeSlideUp {
                    from {opacity: 0; transform: translateY(10px);}
                    to {opacity: 1; transform: none;}
                  }

                  .pm-sublist.fade-in {
                    animation: fadeSlideUp 0.35s ease-out;
                  }

                  .pm-row {
                    position:relative;
                    border-radius:12px;
                    transition:background .25s,backdrop-filter .25s,transform .25s;
                    overflow: hidden;
                  }
                  .pm-row:hover {
                    background:rgba(255,255,255,.01);
                    backdrop-filter:blur(1px);
                    transform:translateX(1px) scale(1.02);
                    overflow: hidden;
                  }

                  @keyframes fadeRowUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: none; }
                  }

                  .pm-row.fade-in-row {
                    backdropFilter: blur(4px);
                  }

                  .chevron {
                    transform:rotate(270deg);
                    transition:transform .35s cubic-bezier(.45,.05,.25,1),fill .35s cubic-bezier(.45,.05,.25,1);
                  }
                `}</style>

                {
                  isLoading && (
                    <div
                      style={{
                        width: '15px',
                        height: '15px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        margin: '0 auto'
                      }}
                    >
                      <LoaderWrapper />
                    </div>
                  )
                }

                {
                  !isLoading && <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 10 }}>
                    {
                      !selectedMethod && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <InputLabel style={{
                          textAlign: 'center',
                          margin: '0 auto',
                          fontSize: 16,
                          paddingBottom: 15,
                          color: 'black',
                          opacity: 0.75
                        }}>
                          {
                            selectedTicker &&
                          <Chevron className="chevron"
                            onClick={() => {
                              setSelectedTicker('')
                              setSelectedChainName('')
                              setSelectedMethod(undefined)
                            }}
                            fill={RADOM_COLORS.GRAY_DARKEST}
                            style={{
                              width: 14,
                              height: 14,
                              transform: 'rotate(90deg)',
                              cursor: 'pointer',
                              marginRight: 5
                            }} />
                          }

                          {
                            !selectedTicker ? 'Select payment currency' : 'Select Payment Network for'
                          }
                          {
                            selectedTicker &&
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <img src={selectedTokenImageUrl ?? ''} style={{
                              height: 25,
                              width: 25,
                              borderRadius: 10,
                              opacity: 1
                            }} />
                            <span style={{ textTransform: 'capitalize', color: 'black' }}>{selectedTicker}</span>
                          </div>
                          }
                        </InputLabel>
                      </div>
                    }

                    <div className="pm-list" style={{
                      border: '1px solid',
                      borderColor: RADOM_COLORS.GRAY_DARK,
                      borderBottom: 'none',
                      borderRadius: 15,
                      maxHeight: 325,
                      overflow: 'hidden',
                      boxShadow: '0 4px 10px rgba(0,0,0,.06)'
                    }}>
                      {props.checkoutData.gateway.managed.methods.length === 0 && (
                        <div style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKER, margin: 20 }}>
                        No available payment methods
                        </div>
                      )}
                      {!selectedTicker && (
                        <>
                          {Array.from(
                            new Map(
                              props.checkoutData.gateway.managed.methods.map(m => {
                                const mm = getMethod(m.network, m.token)
                                if (mm.ticker === 'ETH') {
                                  if (mm.tokenInfo) {
                                    mm.tokenInfo.logo = EthLogo
                                  } else {
                                    mm.logo = EthLogo
                                  }
                                }

                                let discountAmount = 0

                                if (m.discountPercentOff) {
                                  discountAmount = m.discountPercentOff
                                } else if (props.discount) {
                                  const model = props.discount.model
                                  if (model.modelType === 'percentage') {
                                    discountAmount = Number(model.amountOff) / 100
                                  } else if (model.modelType === 'fixed') {
                                    const fixedAmount = Number(model.amountOff)
                                    const converted = convertValue(
                                      fixedAmount,
                                      props.currency,
                                      mm.ticker,
                                      priceQuotes,
                                      false
                                    )
                                    discountAmount = converted / convertValue(
                                      totalSum,
                                      props.currency,
                                      mm.ticker,
                                      priceQuotes,
                                      false
                                    )
                                    discountAmount = Math.min(discountAmount, 1)
                                  }
                                }

                                return [mm.ticker, { method: mm, discountAmount }]
                              })
                            ).values()
                          ).map(({ method, discountAmount }, i, arr) => {
                            const isSelected = selectedTicker === method.ticker
                            const isLast = arr.length >= 3 && i === arr.length - 1

                            return (
                              <div
                                key={i}
                                className={`pm-row ${isLast ? 'fade-in-row' : ''}`}
                                data-selected={isSelected}
                                style={{
                                  padding: 12,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  borderBottom: `1px solid ${RADOM_COLORS.GRAY_MED}`,
                                  cursor: 'pointer'
                                }}
                                onClick={() => {
                                  const willSelect = !isSelected
                                  setSelectedTicker(willSelect ? method.ticker : '')
                                  setSelectedTokenImageUrl(method.logo || '')
                                  setSelectedChainName('')
                                  setSelectedMethod(undefined)
                                }}
                              >
                                <div style={{ display: 'flex', gap: 15 }}>
                                  <div
                                    style={{
                                      width: 30,
                                      height: 30,
                                      borderRadius: 15,
                                      background: `center/contain no-repeat url(${method.logo || ''})`
                                    }}
                                  />
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                      {(() => {
                                        const hasQuotes = !!priceQuotes && priceQuotes.length > 0 &&
                                        !isLoadingPriceQuotes
                                        const baseVal = hasQuotes
                                          ? convertValue(
                                            totalSum,
                                            props.currency,
                                            method.ticker,
                                            priceQuotes,
                                            props.checkoutData.chargeCustomerNetworkFee
                                          )
                                          : null

                                        const discountedVal = hasQuotes
                                          ? convertValue(
                                            totalSum,
                                            props.currency,
                                            method.ticker,
                                            priceQuotes,
                                            props.checkoutData.chargeCustomerNetworkFee,
                                            discountAmount
                                          )
                                          : null

                                        return (
                                          <>
                                            {discountAmount > 0 && (
                                              <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>
                                                {hasQuotes ? formatCrypto8(baseVal as number) : '—'}
                                              </span>
                                            )}

                                            <span>
                                              {hasQuotes
                                                ? formatCrypto8(discountedVal as number)
                                                : (isLoadingPriceQuotes ? <Spinner /> : '—')}
                                            </span>
                                          </>
                                        )
                                      })()}
                                      {discountAmount > 0 && (
                                        <span
                                          style={{
                                            padding: '1px 5px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            background: 'linear-gradient(to right top, rgb(102 233 201), rgb(250 255 231 / 92%))',
                                            border: '1px solid rgb(7 210 7 / 63%)',
                                            boxShadow: 'rgb(7 210 7 / 73%) 0px 0px 3px',
                                            borderRadius: 5,
                                            textTransform: 'uppercase',
                                            color: '#4aa233'
                                          }}
                                        >
                                          {props.discount && props.discount.model.modelType === 'percentage'
                                            ? `${Number(props.discount.model.amountOff)}% off`
                                            : props.discount
                                              ? `-${Number(props.discount.model.amountOff)} ${props.currency}`
                                              : ''}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ color: RADOM_COLORS.INPUT_PLACEHOLDER, fontSize: 12 }}>
                                      {method.ticker}
                                    </div>
                                  </div>
                                </div>
                                <Chevron
                                  className="chevron"
                                  fill={isSelected ? RADOM_COLORS.GRAY_DARKER : RADOM_COLORS.GRAY_DARK}
                                  style={{
                                    width: 15,
                                    height: 15,
                                    transform: isSelected ? 'rotate(0deg)' : 'rotate(270deg)'
                                  }}
                                />
                              </div>
                            )
                          })}
                        </>
                      )}

                      <div className={'pm-sublist'}>
                        {selectedTicker && !selectedMethod && Array.from(
                          new Map(
                            props.checkoutData.gateway.managed.methods
                              .filter(m => getMethod(m.network, m.token).ticker === selectedTicker)
                              .map(m => {
                                const mm = getMethod(m.network, m.token)
                                const uniqueKey = `${m.network}-${m.token ?? 'null'}`
                                return [uniqueKey, { method: mm }]
                              })
                          ).values()
                        ).map(({ method }, i) => {
                          const isSelected = selectedChainName === method.tokenInfo?.networkName
                          return (
                            <div key={i}
                              className="pm-row"
                              data-selected={isSelected}
                              style={{
                                padding: 12,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: `1px solid ${RADOM_COLORS.GRAY_MED}`,
                                cursor: `${(isSelected || isLoading) ? 'not-allowed' : 'pointer'}`,
                                pointerEvents: isLoading ? 'none' : 'auto'
                              }}
                              onClick={async () => {
                                if (isLoading || isSelected) return
                                setSelectedChainName(method.tokenInfo?.networkName)
                                setSelectedMethod(method)

                                setIsLoading(true)
                                try {
                                  if (recurringInterval() > 0) {
                                    setManagedSubscriptionState(SubscriptionCheckoutState.Select)
                                    return
                                  }

                                  await props.onCheckout({
                                    managed: { selectedMethod: method },
                                    priceQuotes,
                                    travelRuleFields
                                  })
                                } catch {}
                              }}
                            >
                              <div style={{ display: 'flex', gap: 15 }}>
                                <div style={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 15,
                                  background: `center/contain no-repeat url(${(method.tokenInfo?.logo ?? method.logo) || ''})`
                                }} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <div style={{ fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>
                                    {method.tokenInfo?.networkName ?? method.name}
                                  </div>
                                  <div style={{ color: RADOM_COLORS.INPUT_PLACEHOLDER, fontSize: 12 }}>
                                    {method.ticker}
                                  </div>
                                </div>
                              </div>
                              <Chevron className="chevron" fill={isSelected ? RADOM_COLORS.GRAY_DARKER : RADOM_COLORS.GRAY_DARK} style={{ width: 15, height: 15 }} />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                }
              </>
            </Container>
          }

          {
            // -----------------------------------------------------------------------------------------------------------
            !props.managedPaymentDetails &&
            managedSubscriptionState === SubscriptionCheckoutState.Select &&
            <Container>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span>Payment method</span>
                {
                  selectedMethod &&
                  <div style={{
                    display: 'flex',
                    gap: 15,
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: 30,
                      height: 30,
                      backgroundImage: `url(${selectedMethod.logo})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      position: 'relative'
                    }}>
                      {
                        selectedMethod.tokenInfo && <div style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '100%',
                          width: 15,
                          height: 15,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'absolute',
                          bottom: -1,
                          right: -5,
                          boxShadow: '0 0 1px'
                        }}>
                          <div style={{
                            width: 10,
                            height: 10,
                            backgroundImage: `url(${selectedMethod.tokenInfo.logo})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }} />
                        </div>
                      }
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                        {selectedMethod.ticker}
                      </span>
                      <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                        {selectedMethod.tokenInfo?.networkName ?? selectedMethod.name}
                      </span>
                    </div>
                  </div>
                }
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>

                <span>Select subscription type</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <PrimaryButton
                    disabled={props.isPreProcessingPayment || isLoadingAllowance || !isAutopayEnabled(selectedMethod)}
                    style={{
                      background: 'none',
                      color: RADOM_COLORS.BLACK,
                      justifyContent: 'flex-start',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      padding: 20,
                      borderRadius: 5,
                      fontSize: 14
                    }}
                    onClick={() => {
                      setSelectedSubscriptionType(SubscriptionType.Autopay)

                      const { emailAddress } = travelRuleFields
                      const { travelRuleEmail } = props.checkoutData
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      const trfEmail = emailAddress || travelRuleEmail || ''

                      setSubscriptionEmailAddress(trfEmail)
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Checkbox isRadio checked={selectedSubscriptionType === SubscriptionType.Autopay} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ textAlign: 'left' }}>Subscribe with auto renew</span>
                          <span style={{ opacity: 0.5, textAlign: 'left' }}>Set an alllowance and automatically pay</span>
                        </div>
                      </div>
                    </div>
                  </PrimaryButton>

                  <PrimaryButton
                    disabled={props.isPreProcessingPayment || isLoadingAllowance}
                    style={{
                      background: 'none',
                      color: RADOM_COLORS.BLACK,
                      justifyContent: 'flex-start',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      padding: 20,
                      borderRadius: 5,
                      fontSize: 14
                    }}
                    onClick={() => {
                      setSelectedSubscriptionType(SubscriptionType.EmailInvoice)
                      // Fill the subscription invoice email address with the travel rule fields email address
                      // If the email address has not been collected as part of the travel rule fields, then its because
                      // we already have that value from a previous checkout and therefore it has been supplied by the
                      // backend
                      const { emailAddress } = travelRuleFields
                      const { travelRuleEmail } = props.checkoutData
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      const trfEmail = emailAddress || travelRuleEmail || ''

                      setSubscriptionEmailAddress(trfEmail)
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Checkbox isRadio checked={selectedSubscriptionType === SubscriptionType.EmailInvoice} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ textAlign: 'left' }}>Subscribe with email invoices</span>
                          <span style={{ opacity: 0.5, textAlign: 'left' }}>Manually pay using email invoices</span>
                        </div>
                      </div>
                    </div>
                  </PrimaryButton>
                </div>

                {
                  selectedSubscriptionType === SubscriptionType.EmailInvoice &&
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <span>Email address</span>
                    <TextInput
                      autoFocus
                      placeholder='Enter email address'
                      disabled={subscriptionEmailAddress.length > 0}
                      value={subscriptionEmailAddress}
                      onChange={e => setSubscriptionEmailAddress(e.target.value.toLowerCase())} />
                  </div>
                }

                {
                  selectedSubscriptionType === SubscriptionType.Autopay &&
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setReceiveEmailNotifications(!receiveEmailNotifications)}>
                      <Checkbox checked={receiveEmailNotifications} />
                      <span>Send me email notifications about my subscription</span>
                    </div>

                    {
                      receiveEmailNotifications &&
                      <TextInput
                        autoFocus
                        placeholder='Enter email address'
                        disabled={subscriptionEmailAddress.length > 0}
                        value={subscriptionEmailAddress}
                        onChange={e => setSubscriptionEmailAddress(e.target.value.toLowerCase())} />
                    }
                  </div>
                }
              </div>

              {
                selectedSubscriptionType === SubscriptionType.Autopay && address &&
                <ConnectedWalletContainer>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderRadius: 5
                    }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: 15,
                      backgroundColor: RADOM_COLORS.GRAY_MED
                    }}>
                      <span style={{ fontSize: 14 }}>Connected wallet</span>
                      <SecondaryButton
                        style={{
                          border: 0,
                          padding: 0,
                          color: RADOM_COLORS.BLACK,
                          fontSize: 10,
                          textTransform: 'uppercase'
                        }}
                        onClick={() => disconnect()}>
                        Disconnect
                      </SecondaryButton>
                    </div>
                    <span style={{ padding: 15 }}>{address}</span>
                  </div>
                </ConnectedWalletContainer>
              }

              <PrimaryButton
                isLoading={
                  isLoadingPriceQuotes ||
                  isSettingAllowance ||
                  props.isPreProcessingPayment ||
                  isLoadingAllowance
                }
                disabled={
                  selectedSubscriptionType === undefined ||
                  (selectedSubscriptionType === SubscriptionType.EmailInvoice && !subscriptionEmailAddress)
                }
                style={{ background: props.checkoutData.customizations?.primaryButtonColor, color: getContrastColor(props.checkoutData.customizations?.primaryButtonColor), width: '100%', margin: 'auto' }}
                onClick={async () => {
                  if (selectedSubscriptionType === SubscriptionType.Autopay && !isConnected) {
                    return await open()
                  }

                  if (!selectedMethod || selectedSubscriptionType === undefined) {
                    return
                  }

                  props.onCheckout({
                    managed: {
                      selectedMethod
                    },
                    priceQuotes,
                    subscriptionOptions: {
                      autopay: selectedSubscriptionType === SubscriptionType.Autopay
                        ? {
                          subscriptionType: selectedSubscriptionType,
                          address: address as any
                        }
                        : undefined,
                      emailInvoice: selectedSubscriptionType === SubscriptionType.EmailInvoice
                        ? {
                          subscriptionType: selectedSubscriptionType,
                          emailAddress: subscriptionEmailAddress.toLowerCase()
                        }
                        : undefined
                    },
                    travelRuleFields
                  })
                }}>
                {
                  selectedSubscriptionType === SubscriptionType.Autopay && !isConnected
                    ? 'Approve payment'
                    : 'Continue'
                }
              </PrimaryButton>

              <SecondaryButton
                disabled={isLoadingPriceQuotes || isSettingAllowance ||
                  isLoadingAllowance || props.isPreProcessingPayment}
                style={{ border: 0, padding: 5, color: RADOM_COLORS.GRAY_DARKER }}
                onClick={() => {
                  console.log('resetting error message')
                  setManagedSubscriptionState(undefined)
                  setSelectedMethod(undefined)
                }}>
                Change payment method
              </SecondaryButton>
            </Container>
          }

          {
            // -----------------------------------------------------------------------------------------------------------
            !props.managedPaymentDetails &&
            (managedSubscriptionState === SubscriptionCheckoutState.Allowance ||
              managedSubscriptionState === SubscriptionCheckoutState.Confirm) &&
            selectedMethod &&
            <Container style={{ gap: 30 }}>
              <div style={{
                display: 'flex',
                userSelect: 'none',
                alignItems: 'center',
                gap: 10,
                padding: 15,
                borderRadius: 5,
                border: `1px solid ${RADOM_COLORS.GRAY_MED}`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                  padding: '0 20px'
                }}>
                  <span style={{
                    display: 'flex',
                    borderRadius: '100%',
                    width: 20,
                    height: 20,
                    backgroundColor: managedSubscriptionState === SubscriptionCheckoutState.Confirm
                      ? RADOM_COLORS.SUCCESS_LIGHT
                      : RADOM_COLORS.NICE_GREEN,
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'white',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: '0.2s ease all'
                  }}>1</span>
                  <span style={{
                    fontSize: 16,
                    fontWeight: managedSubscriptionState === SubscriptionCheckoutState.Confirm ? 300 : 400,
                    color: managedSubscriptionState === SubscriptionCheckoutState.Confirm
                      ? RADOM_COLORS.GRAY_DARKEST
                      : RADOM_COLORS.BLACK,
                    transition: '0.2s ease all'
                  }}>Set allowance</span>
                </div>

                <div style={{
                  width: '100%',
                  backgroundColor: RADOM_COLORS.GRAY_DARK,
                  height: 2,
                  borderRadius: 2,
                  flexGrow: 1,
                  transition: '0.2s ease all'
                }} />
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                  padding: '0 20px'
                }}>
                  <span style={{
                    display: 'flex',
                    borderRadius: '100%',
                    width: 20,
                    height: 20,
                    backgroundColor: managedSubscriptionState === SubscriptionCheckoutState.Confirm
                      ? RADOM_COLORS.NICE_GREEN
                      : RADOM_COLORS.GRAY_DARKER,
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'white',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: '0.2s ease all'
                  }}>2</span>
                  <span style={{
                    fontSize: 16,
                    fontWeight: managedSubscriptionState === SubscriptionCheckoutState.Confirm ? 400 : 300
                  }}>Subscribe</span>

                </div>
              </div>

              {
                managedSubscriptionState === SubscriptionCheckoutState.Allowance && <>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    color: RADOM_COLORS.BLACK,
                    borderRadius: 5
                  }}>
                    <span>Set an allowance to enable automatically recurring payments for your subscription.</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <PrimaryButton
                      isLoading={isLoadingPriceQuotes || isSettingAllowance}
                      style={{
                        background: props.checkoutData.customizations?.primaryButtonColor,
                        width: '100%',
                        margin: 'auto'
                      }}
                      onClick={() => {
                        setManagedSubscriptionAllowance()
                      }}>
                      Set allowance
                    </PrimaryButton>

                    {
                      allowSkipAllowance &&
                      <SecondaryButton
                        disabled={isLoadingPriceQuotes || isSettingAllowance}
                        style={{
                          padding: 5,
                          color: RADOM_COLORS.GRAY_DARKER,
                          borderColor: RADOM_COLORS.GRAY_DARK
                        }}
                        onClick={() => setManagedSubscriptionState(SubscriptionCheckoutState.Confirm)}>
                        Skip
                      </SecondaryButton>
                    }

                    <SecondaryButton
                      disabled={isLoadingPriceQuotes || isSettingAllowance}
                      style={{ border: 0, padding: 5, color: RADOM_COLORS.GRAY_DARKER }}
                      onClick={() => {
                        setManagedSubscriptionState(undefined)
                        setSelectedMethod(undefined)
                      }}>
                      Change payment method
                    </SecondaryButton>
                  </div>
                </>
              }

              {
                managedSubscriptionState === SubscriptionCheckoutState.Confirm &&
                <Container>
                  <div style={{ boxShadow: `0 0 3px ${RADOM_COLORS.GRAY_DARK}`, borderRadius: 5 }}>
                    <div style={{
                      backgroundColor: RADOM_COLORS.GRAY_MED,
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderTopLeftRadius: 5,
                      borderTopRightRadius: 5,
                      padding: 10
                    }}>Initial payment</div>
                    <div style={{
                      display: 'flex',
                      gap: 15,
                      alignItems: 'center',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      padding: 15,
                      borderTop: 0,
                      borderBottomLeftRadius: 5,
                      borderBottomRightRadius: 5
                    }}>
                      <div style={{
                        width: 30,
                        height: 30,
                        backgroundImage: `url(${selectedMethod.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }}>
                        {
                          selectedMethod.tokenInfo && <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '100%',
                            width: 15,
                            height: 15,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            bottom: -1,
                            right: -5,
                            boxShadow: '0 0 1px'
                          }}>
                            <div style={{
                              width: 10,
                              height: 10,
                              backgroundImage: `url(${selectedMethod.tokenInfo.logo})`,
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }} />
                          </div>
                        }
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>

                          <span style={{ fontSize: 16 }}>
                            {
                              isLoadingPriceQuotes
                                ? <Spinner />
                                : formatCrypto8(
                                  convertValue(totalSum, props.currency, selectedMethod.ticker, priceQuotes, false)
                                )
                            }
                          </span>

                          {
                            props.checkoutData.chargeCustomerNetworkFee &&
                            <>
                              <span>+</span>

                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                backgroundColor: RADOM_COLORS.GRAY_MED,
                                borderRadius: 5,
                                padding: '2px 6px',
                                color: RADOM_COLORS.BLACK
                              }}>
                                <GasPump style={{ height: 10 }} fill={RADOM_COLORS.BLACK} />
                                <span>{gasCost}</span>
                              </div>
                            </>
                          }

                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                          <span style={{ fontSize: 12, color: RADOM_COLORS.GRAY_DARKER }}>
                            {selectedMethod.ticker}
                          </span>
                          <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                            on {selectedMethod.tokenInfo?.networkName ?? selectedMethod.name}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>

                  <div style={{ boxShadow: `0 0 3px ${RADOM_COLORS.GRAY_DARK}`, borderRadius: 5 }}>
                    <div style={{
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderBottom: 0,
                      borderTopLeftRadius: 5,
                      borderTopRightRadius: 5,
                      padding: 10,
                      color: RADOM_COLORS.GRAY_DARKEST,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST
                    }}>Recurring payments</div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 20px',
                      color: RADOM_COLORS.GRAY_DARKEST,
                      borderTop: 0,
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderBottomLeftRadius: 5,
                      borderBottomRightRadius: 5
                    }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        backgroundImage: `url(${selectedMethod.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }}>
                        {
                          selectedMethod.tokenInfo && <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '100%',
                            width: 12,
                            height: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            bottom: -1,
                            right: -5,
                            boxShadow: '0 0 1px'
                          }}>
                            <div style={{
                              width: 8,
                              height: 8,
                              backgroundImage: `url(${selectedMethod.tokenInfo.logo})`,
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }} />
                          </div>
                        }
                      </div>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {formatCrypto8(
                          convertValue(recurringSum, props.currency, selectedMethod.ticker, priceQuotes,
                            false)
                        )} {
                          props.checkoutData.chargeCustomerNetworkFee &&
                          `+ gas fee per ${formatSubscriptionDuration(recurringInterval() * 60 * 60 * 24).toLowerCase()}`
                        }
                        <span>starting</span>
                        {nextBillingAt()}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <PrimaryButton
                      isLoading={
                        isLoadingPriceQuotes || props.isPreProcessingPayment || isSigningMessage
                      }
                      style={{
                        background: props.checkoutData.customizations?.primaryButtonColor,
                        width: '100%',
                        margin: 'auto'
                      }}
                      onClick={async () => {
                        if (!props.tokenDelegatePaymentDetails || !address) {
                          return
                        }

                        setIsSigningMessage(true)

                        try {
                          const chain = NetworkToChainMap[props.tokenDelegatePaymentDetails.network] as any
                          const provider: any = await connector?.getProvider()

                          const signer = createWalletClient({
                            chain,
                            transport: custom(provider)
                          })

                          const signature = await signer?.signMessage({
                            account: address,
                            message: props.tokenDelegatePaymentDetails.messageToSign
                          })

                          props.onCheckout({
                            travelRuleFields,
                            completeTokenDelegate: {
                              paymentId: props.tokenDelegatePaymentDetails.paymentId,
                              signature
                            },
                            priceQuotes
                          })
                        } catch (err) {
                          errorToast(err.toString())
                        }

                        setIsSigningMessage(false)
                      }}>
                      Subscribe
                    </PrimaryButton>

                    <SecondaryButton
                      style={{ border: 0, padding: 5, color: RADOM_COLORS.GRAY_DARKER }}
                      disabled={isLoadingPriceQuotes || props.isPreProcessingPayment || isSigningMessage}
                      onClick={() => { setManagedSubscriptionState(undefined); setSelectedMethod(undefined) }}>
                      Change payment method
                    </SecondaryButton>
                  </div>

                </Container>
              }

            </Container>
          }

          {
            // -----------------------------------------------------------------------------------------------------------
            props.managedPaymentDetails &&
            <Container style={{
              overflowWrap: 'anywhere',
              ...(props.posMode && {
                alignItems: 'center',
                gap: 60,
                overflowWrap: 'normal'
              })
            }}>
              {
                props.posMode &&
                <WaveContainer>
                  <Wave />
                  <Wave2 />
                  <Wave3 />
                </WaveContainer>
              }
              <Styles />
              {
                props.posMode &&
                <div style={{ display: 'flex', flexDirection: 'column', fontSize: 12, gap: 3, alignItems: 'center' }}>
                  <span style={{ opacity: 0.5, textTransform: 'uppercase', textAlign: props.posMode ? 'center' : undefined }}>Time remaining</span>
                  <span style={{ fontSize: 16, fontWeight: 100, textAlign: 'center' }}>
                    {
                      formatSecondsToMinutesAndSecondsString(
                        (new Date(
                          props.paymentStatus?.expiresAt ??
                          props.managedPaymentDetails.expiresAt
                        ).getTime() - new Date().getTime()) / 1000
                      )
                    }
                  </span>
                </div>
              }
              <div>
                <div style={{
                  fontSize: 20,
                  fontWeight: 500,
                  textAlign: 'center',
                  color: RADOM_COLORS.GRAY_DARKER,
                  ...(props.posMode && { marginBottom: 20, marginTop: 10 })
                }}>
                  Pay with <img
                    src={
                      getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).logo
                    }
                    style={{
                      height: 20,
                      width: 'auto',
                      paddingLeft: 5
                    }}
                  /> <span style={{ color: 'black' }}>{getMethod(
                    props.managedPaymentDetails.network,
                    props.managedPaymentDetails.token
                  ).ticker}</span> on
                  <span style={{ color: 'black', marginLeft: 5 }}>{
                    getMethod(
                      props.managedPaymentDetails.network,
                      props.managedPaymentDetails.token
                    )?.tokenInfo?.networkName ?? getMethod(
                      props.managedPaymentDetails.network,
                      props.managedPaymentDetails.token
                    )?.name
                  }</span> network
                </div>
                <div style={{
                  fontSize: 14,
                  textAlign: 'center',
                  color: RADOM_COLORS.GRAY_DARKER,
                  marginTop: 10
                }}>
                  Connect to your wallet, scan the QR code or copy the address to make a payment.
                </div>
              </div>
              {
                props.paymentStatus?.status !== 'expired' &&
                props.checkoutData.customizations?.displayQrCode !== false &&
                <QRCodeWrapper style={{
                  margin: '0 auto'
                }}>
                  <QRCodeWrapperInner>
                    <img src={managedPaymentQrCode} style={{ width: 150, height: 150 }} />
                  </QRCodeWrapperInner>
                </QRCodeWrapper>
              }
              <div style={{
                padding: '10px 15px',
                borderRadius: 20,
                margin: '0 auto',
                backgroundColor: RADOM_COLORS.BLUE7,
                color: RADOM_COLORS.BLUE5
              }}>
                Time Remaining <span style={{ color: RADOM_COLORS.BLUE4, fontWeight: 500 }}>
                  {formatSecondsToMinutesAndSecondsString(
                    (new Date(
                      props.paymentStatus?.expiresAt ??
                      props.managedPaymentDetails.expiresAt
                    ).getTime() - new Date().getTime()) / 1000
                  )}
                </span>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                ...(props.posMode && { gap: 40 })
              }}>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontSize: 12,
                  gap: 3,
                  ...(props.posMode && { alignItems: 'center' })
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ opacity: 0.5, textTransform: 'uppercase' }}>Payment address</span>
                    {
                      props.paymentStatus?.status !== 'expired' &&
                      <CopyButton onClick={async () =>
                        await navigator.clipboard.writeText(props.managedPaymentDetails?.paymentAddress ?? '')} />
                    }
                  </div>
                  <span style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 2, fontWeight: 100 }}>
                    {props.paymentStatus?.status === 'expired'
                      ? 'Expired'
                      : props.managedPaymentDetails.paymentAddress
                    }
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '20px 40px', flexWrap: 'wrap' }}>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: 12,
                    gap: 3,
                    flexGrow: 1,
                    ...(props.posMode && { alignItems: 'center' })
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ opacity: 0.5, textTransform: 'uppercase' }}>Amount due</span>
                      {
                        props.paymentStatus?.status !== 'expired' &&
                        <CopyButton onClick={async () =>
                          await navigator.clipboard.writeText(formatCrypto8(props.managedPaymentDetails?.amount as any)
                          )}
                        />
                      }
                    </div>

                    <div style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 300 }}>
                      <img
                        src={getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).logo}
                        style={{ height: 20, width: 'auto' }}
                      />
                      {
                        formatCrypto8(props.managedPaymentDetails.amount as any)
                      }
                      <span style={{ opacity: 0.5, fontWeight: 100 }}>
                        {getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).ticker}
                      </span>
                    </div>
                  </div>
                </div>

                {/* {
                !props.posMode &&
                <div style={{ display: 'flex', flexDirection: 'column', fontSize: 12, gap: 3 }}>
                  <span style={{ opacity: 0.5, textTransform: 'uppercase' }}>Time remaining</span>
                  <span style={{ fontSize: 16, fontWeight: 100 }}>
                    {
                      props.paymentStatus?.status === 'expired'
                        ? 'Expired'
                        : formatSecondsToMinutesAndSecondsString(
                          (
                            new Date(
                              props.paymentStatus?.expiresAt ??
                              props.managedPaymentDetails.expiresAt
                            ).getTime() -
                            new Date().getTime()) /
                          1000
                        )
                    }
                  </span>
                </div>
              } */}

                {
                  props.paymentStatus && props.paymentStatus.transactions.length > 0 &&
                  <div style={{ borderRadius: 15, border: `1px solid ${RADOM_COLORS.GRAY_MED}`, overflow: 'hidden' }}>
                    <div style={{ padding: 15, backgroundColor: 'rgb(255 223 230)' }}>Underpaid payment</div>
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
                      <div>We have received your payment, but it was under the required amount.
                        Please send the remaining balance to complete your order.</div>
                      <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_DARK }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ opacity: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Amount remaining</span>
                        <span>

                          <div style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 300 }}>
                            <img
                              src={
                                getMethod(
                                  props.managedPaymentDetails.network,
                                  props.managedPaymentDetails.token).logo
                              }
                              style={{ height: 20, width: 'auto' }}
                            />
                            {
                              formatCrypto8(
                                props.paymentStatus.amountRemaining as any)
                            }
                            <span style={{ opacity: 0.5, fontWeight: 100 }}>
                              {getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).ticker}
                            </span>
                          </div>
                        </span>
                      </div>
                    </div>
                  </div>
                }

                {
                  props.paymentStatus &&
                  props.paymentStatus.pendingTransactions.length > 0 &&
                  props.paymentStatus.transactions.length <= 0 &&
                  <PaymentDetectedContainer>
                    <div style={{ padding: 15, backgroundColor: RADOM_COLORS.GRAY_MED }}>Payment detected</div>
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
                      <div>Your payment has been detected and is currently pending confirmation.</div>
                      <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_DARK }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ opacity: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Amount paid</span>
                        <span>

                          <div style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 300 }}>
                            <img
                              src={
                                getMethod(
                                  props.managedPaymentDetails.network,
                                  props.managedPaymentDetails.token).logo
                              }
                              style={{ height: 20, width: 'auto' }}
                            />
                            {
                              formatCrypto8(
                                props.paymentStatus.pendingTransactions.reduce((s, t) => s + Number(t.amount), 0)
                              )
                            }
                            <span style={{ opacity: 0.5, fontWeight: 100 }}>
                              {getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).ticker}
                            </span>
                          </div>
                        </span>
                      </div>
                    </div>
                  </PaymentDetectedContainer>
                }

                {
                  !props.posMode && props.paymentStatus?.status !== 'expired' &&
                  props.checkoutData.organizationId !== 'f58cf6b3-b87d-4893-b859-cddc85ac0abf' && !isBitcoinNetwork &&
                  <div style={{
                    width: '100%',
                    borderTop: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                    paddingTop: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 20
                  }}>

                    {
                      !selectedMethod?.isSolana && <>
                        {
                          isConnected &&
                          <ConnectedWalletContainer>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                                borderRadius: 5
                              }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: 15,
                                backgroundColor: RADOM_COLORS.GRAY_MED
                              }}>
                                <span style={{ fontSize: 14 }}>Connected wallet</span>
                                <SecondaryButton
                                  style={{
                                    border: 0,
                                    padding: 0,
                                    color: RADOM_COLORS.BLACK,
                                    fontSize: 10,
                                    textTransform: 'uppercase'
                                  }}
                                  onClick={() => disconnect()}>
                                  Disconnect
                                </SecondaryButton>
                              </div>
                              <span style={{ padding: 15 }}>{address}</span>
                            </div>
                          </ConnectedWalletContainer>
                        }
                        <PrimaryButton
                          isLoading={isManagedEVMTxLoading}
                          onClick={onClickOpenWallet}
                          style={{
                            width: '100%',
                            background: props.checkoutData.customizations?.primaryButtonColor,
                            color: getContrastColor(props.checkoutData.customizations?.primaryButtonColor)
                          }}>
                          {
                            isConnected ? 'Approve payment' : 'Connect wallet'
                          }
                        </PrimaryButton>
                      </>
                    }

                    {
                      selectedMethod?.isSolana &&
                      <SolanaWallet
                        managedPaymentDetails={props.managedPaymentDetails}
                        isLoading={isManagedEVMTxLoading}
                        setIsLoading={setIsManagedEVMTxLoading}
                        primaryButtonColor={props.checkoutData.customizations?.primaryButtonColor} />
                    }
                  </div>
                }

                {
                  !props.posMode && props.paymentStatus?.status === 'expired' &&
                  <div style={{
                    width: '100%',
                    borderTop: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                    paddingTop: 20
                  }}>
                    <PrimaryButton
                      onClick={() => window.location.reload()}
                      style={{
                        width: '100%',
                        background: props.checkoutData.customizations?.primaryButtonColor
                      }}>
                      Reload
                    </PrimaryButton>
                  </div>
                }
              </div>

            </Container>
          }

        </Container>
      }
    </>
    }

  </Wrapper>
}

export default PayWidget
